import React, { useState, useRef, useCallback, useEffect } from "react";

const FORMATS = [
  { id: "square", label: "Square", sub: "1:1", w: 1080, h: 1080 },
  { id: "portrait", label: "Portrait", sub: "4:5", w: 1080, h: 1350 },
  { id: "landscape", label: "Landscape", sub: "1.91:1", w: 1080, h: 566 },
  { id: "story", label: "Story / Reel", sub: "9:16", w: 1080, h: 1920 },
];

function readFileAsDataURL(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.src = src;
  });
}

function cropImageToBlob(dataUrl, format, fit, offsetX, offsetY) {
  return new Promise(async (resolve) => {
    const img = await loadImage(dataUrl);
    const canvas = document.createElement("canvas");
    canvas.width = format.w;
    canvas.height = format.h;
    const ctx = canvas.getContext("2d");
    const targetAR = format.w / format.h;
    const srcAR = img.naturalWidth / img.naturalHeight;

    if (fit === "cover") {
      let sw, sh, sx, sy;
      if (srcAR > targetAR) {
        sh = img.naturalHeight;
        sw = sh * targetAR;
        sy = 0;
        sx = (img.naturalWidth - sw) * offsetX;
      } else {
        sw = img.naturalWidth;
        sh = sw / targetAR;
        sx = 0;
        sy = (img.naturalHeight - sh) * offsetY;
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, format.w, format.h);
    } else {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, format.w, format.h);
      let dw, dh, dx, dy;
      if (srcAR > targetAR) {
        dw = format.w;
        dh = dw / srcAR;
        dx = 0;
        dy = (format.h - dh) / 2;
      } else {
        dh = format.h;
        dw = dh * srcAR;
        dy = 0;
        dx = (format.w - dw) / 2;
      }
      ctx.drawImage(img, dx, dy, dw, dh);
    }

    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.95);
  });
}

function PreviewCard({ image, format, fit, onRemove, onOffsetChange }) {
  const ar = format.w / format.h;
  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const handleMouseDown = (e) => {
    if (fit !== "cover") return;
    dragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };

    const containerW = containerRef.current?.offsetWidth || 200;
    const containerH = containerRef.current?.offsetHeight || 200;
    const imgAR = image.naturalW / image.naturalH;
    const targetAR = format.w / format.h;

    let moveX = 0, moveY = 0;
    if (imgAR > targetAR) {
      const scaledW = containerH * imgAR;
      const overflow = scaledW - containerW;
      if (overflow > 0) moveX = -dx / overflow;
    } else {
      const scaledH = containerW / imgAR;
      const overflow = scaledH - containerH;
      if (overflow > 0) moveY = -dy / overflow;
    }

    onOffsetChange(
      image.id,
      Math.max(0, Math.min(1, image.offsetX + moveX)),
      Math.max(0, Math.min(1, image.offsetY + moveY))
    );
  }, [fit, image, format, onOffsetChange]);

  const handleMouseUp = () => { dragging.current = false; };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove]);

  return (
    <div style={{ background: "#1a1a1a", borderRadius: 12, overflow: "hidden", border: "1px solid #2a2a2a", display: "flex", flexDirection: "column" }}>
      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        style={{
          position: "relative",
          width: "100%",
          paddingTop: `${(1 / ar) * 100}%`,
          overflow: "hidden",
          cursor: fit === "cover" ? "grab" : "default",
          background: "#111",
        }}
      >
        <img
          src={image.url}
          alt={image.name}
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: fit === "cover" ? "cover" : "contain",
            objectPosition: fit === "cover" ? `${image.offsetX * 100}% ${image.offsetY * 100}%` : "center",
            background: fit === "contain" ? "#000" : "transparent",
            userSelect: "none",
            pointerEvents: "none",
          }}
        />
      </div>
      <div style={{ padding: "8px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 11, color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {image.name}
        </span>
        {fit === "cover" && <span style={{ fontSize: 10, color: "#444", flexShrink: 0 }}>drag to reposition</span>}
        <button
          onClick={() => onRemove(image.id)}
          style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px", flexShrink: 0 }}
          onMouseEnter={e => e.target.style.color = "#e1306c"}
          onMouseLeave={e => e.target.style.color = "#555"}
        >×</button>
      </div>
    </div>
  );
}

export default function App() {
  const [images, setImages] = useState([]);
  const [formatId, setFormatId] = useState("square");
  const [fit, setFit] = useState("cover");
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus] = useState("");

  const format = FORMATS.find(f => f.id === formatId);

  const addFiles = useCallback(async (files) => {
    const imgFiles = Array.from(files).filter(f => f.type.startsWith("image/"));
    for (const file of imgFiles) {
      const url = await readFileAsDataURL(file);
      const img = await loadImage(url);
      setImages(prev => [...prev, {
        id: Math.random().toString(36).slice(2),
        name: file.name,
        url,
        naturalW: img.naturalWidth,
        naturalH: img.naturalHeight,
        offsetX: 0.5,
        offsetY: 0.5,
      }]);
    }
  }, []);

  const updateOffset = useCallback((id, x, y) => {
    setImages(prev => prev.map(i => i.id === id ? { ...i, offsetX: x, offsetY: y } : i));
  }, []);

  const handleDownloadAll = async () => {
    if (!images.length) return;
    setStatus("Downloading...");
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      setStatus(`Downloading ${i + 1} of ${images.length}...`);
      const blob = await cropImageToBlob(img.url, format, fit, img.offsetX, img.offsetY);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const base = img.name.replace(/\.[^.]+$/, "");
      a.download = `${base}_${format.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      await new Promise(r => setTimeout(r, 200));
    }
    setStatus("");
  };

  const g = "linear-gradient(135deg, #E1306C, #833AB4)";

  return (
    <div style={{ minHeight: "100vh", background: "#0f0f0f", color: "#fff", fontFamily: "system-ui, sans-serif", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: "16px 24px", borderBottom: "1px solid #1e1e1e", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: g, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>✂</div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, background: g, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>CropGrid</div>
          <div style={{ fontSize: 11, color: "#555" }}>Bulk crop for Instagram</div>
        </div>
      </div>

      <div style={{ display: "flex", flex: 1 }}>
        {/* Sidebar */}
        <div style={{ width: 220, flexShrink: 0, borderRight: "1px solid #1e1e1e", padding: "20px 14px", display: "flex", flexDirection: "column", gap: 20 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", color: "#555", textTransform: "uppercase", marginBottom: 8 }}>Format</div>
            {FORMATS.map(f => (
              <button key={f.id} onClick={() => setFormatId(f.id)} style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", marginBottom: 5,
                borderRadius: 8, border: f.id === formatId ? "1px solid #E1306C55" : "1px solid #222",
                background: f.id === formatId ? "rgba(225,48,108,0.12)" : "#161616",
                cursor: "pointer", textAlign: "left",
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: f.id === formatId ? "#fff" : "#999" }}>{f.label}</div>
                  <div style={{ fontSize: 10, color: "#555" }}>{f.sub}</div>
                </div>
              </button>
            ))}
          </div>

          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", color: "#555", textTransform: "uppercase", marginBottom: 8 }}>Crop Mode</div>
            <div style={{ display: "flex", gap: 6 }}>
              {[{id:"cover",label:"Fill",desc:"Crop to fill"},{id:"contain",label:"Fit",desc:"Letterbox"}].map(m => (
                <button key={m.id} onClick={() => setFit(m.id)} style={{
                  flex: 1, padding: "8px 6px", borderRadius: 7,
                  border: m.id === fit ? "1px solid #E1306C66" : "1px solid #222",
                  background: m.id === fit ? "rgba(225,48,108,0.1)" : "#161616",
                  cursor: "pointer", color: m.id === fit ? "#fff" : "#666", fontSize: 12, fontWeight: m.id === fit ? 600 : 400,
                }}>
                  <div>{m.label}</div>
                  <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>{m.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ fontSize: 11, color: "#444", padding: "10px 12px", background: "#141414", borderRadius: 8, border: "1px solid #1e1e1e", lineHeight: 1.6 }}>
            Output: {format.w} × {format.h}px<br />
            {images.length} image{images.length !== 1 ? "s" : ""} loaded
          </div>

          <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={handleDownloadAll} disabled={!images.length || !!status} style={{
              padding: "12px", borderRadius: 9, border: "none",
              background: images.length && !status ? g : "#1e1e1e",
              color: images.length && !status ? "#fff" : "#444",
              fontWeight: 600, fontSize: 13,
              cursor: images.length && !status ? "pointer" : "not-allowed",
            }}>
              {status || `Download All (${images.length})`}
            </button>
            {images.length > 0 && (
              <button onClick={() => setImages([])} style={{ padding: "9px", borderRadius: 9, border: "1px solid #222", background: "none", color: "#555", fontSize: 12, cursor: "pointer" }}>
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Main */}
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          <label
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            style={{
              display: "block", border: dragOver ? "2px dashed #E1306C" : "2px dashed #252525",
              borderRadius: 12, padding: "32px 20px", textAlign: "center", cursor: "pointer",
              marginBottom: images.length ? 20 : 0,
              background: dragOver ? "rgba(225,48,108,0.05)" : "transparent",
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>⬆</div>
            <div style={{ fontSize: 14, color: "#888", fontWeight: 500 }}>Drop images here or click to browse</div>
            <div style={{ fontSize: 11, color: "#444", marginTop: 4 }}>JPG, PNG, WEBP — any quantity</div>
            <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => addFiles(e.target.files)} />
          </label>

          {images.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
              {images.map(img => (
                <PreviewCard
                  key={img.id}
                  image={img}
                  format={format}
                  fit={fit}
                  onRemove={id => setImages(prev => prev.filter(i => i.id !== id))}
                  onOffsetChange={updateOffset}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
